import Modernbanc, { InputElementEvent, InputElementOptions } from '../../lib';
import { useEffect, useState } from 'react';
import { CardLogo } from './card-logo/card-logo.component';
import { input_style } from './styles';
import { API_KEY } from '../constants';
import S, { Chevron, Header, Main, ResultComponent, Row } from './styles';

const cvc_regex = /\d{3}/;
const number_likely_visa_regex = /^4(\d|\s){0,15}$/;
const number_likely_mastercard_regex =
  /^(5[1-5](\d|\s){0,14}|222[1-9](\d|\s){0,11}|22[3-9](\d|\s){0,12}|2[3-6](\d|\s){0,13}|27[01](\d|\s){0,12}|2720(\d|\s){0,11})$/;
const number_likely_amex_regex = /^3[47](\d|\s){0,13}$/;
const google_font = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500&display=swap';

const number_props: InputElementOptions = {
  type: 'input',
  id: 'card_number',
  css: input_style.toString(),
  replacers: [{ type: 'card_number' }],
  google_font_url: google_font,
  matchers: [
    { type: 'valid_card_number' },
    { type: 'card_type' },
    {
      type: 'regex',
      pattern: number_likely_visa_regex.source, // Matches Visa starting with 4, up to one less than the minimum length
      name: 'likely_visa',
    },
    {
      type: 'regex',
      pattern: number_likely_mastercard_regex.source, // Matches Mastercard, up to one less than the full length
      name: 'likely_mastercard',
    },
    {
      type: 'regex',
      pattern: number_likely_amex_regex.source, // Matches American Express, up to one less than the full length
      name: 'likely_american_express',
    },
  ],
  placeholder: '0000 0000 0000 0000',
  variables: { external_id: '4444' },
};
const exp_date_props: InputElementOptions = {
  type: 'input',
  id: 'expiry_date',
  placeholder: 'MM/YY',
  replacers: [{ type: 'expiry_date' }],
  matchers: [{ type: 'valid_expiry_date' }],
  css: input_style.toString(),
  google_font_url: google_font,
};
const cvc_props: InputElementOptions = {
  type: 'input',
  id: 'cvc',
  placeholder: 'CVC',
  css: input_style.toString(),
  matchers: [{ type: 'regex', pattern: cvc_regex.source, name: 'valid_cvc' }],
  google_font_url: google_font,
  max_length: 3,
};

type TCardType = 'visa' | 'mastercard' | 'american-express' | 'other';

const Input = () => {
  const [mdb, setMdb] = useState<Modernbanc>();
  const [loading, setLoading] = useState(false);
  const [card_type, setCardType] = useState<TCardType>();
  const [success, setSuccess] = useState<boolean>();
  const [card_number_ready, setCardNumberReady] = useState(false);
  const [expiry_date_ready, setExpiryDateReady] = useState(false);
  const [cvc_ready, setCvcReady] = useState(false);
  const [valid_card_number, setValidCardNumber] = useState(false);
  const [valid_expiry_date, setValidExpiryDate] = useState(false);
  const [valid_cvc, setValidCvc] = useState(false);
  const can_submit = valid_card_number && valid_expiry_date && valid_cvc;
  const all_inputs_ready = card_number_ready && expiry_date_ready && cvc_ready;
  const deletion_date = new Date();
  deletion_date.setDate(deletion_date.getDate() + 1);
  // const card_number_element = mdb?.getElement('input', number_props.id);

  const show_form = all_inputs_ready && success === undefined && !loading;

  async function submit(e: React.FormEvent<HTMLButtonElement>) {
    e.preventDefault();
    if (!mdb) return;

    setLoading(true);

    // This will create token from each input's value.
    const number_promise = mdb.getElement('input', number_props.id)?.createSecret();
    const exp_date_promise = mdb.getElement('input', exp_date_props.id)?.createSecret();
    const cvc_promise = mdb.getElement('input', cvc_props.id)?.createSecret();

    const promises = [number_promise, exp_date_promise, cvc_promise];
    try {
      const [number_result, exp_date_result, cvc_result] = await Promise.all(promises);
      console.log('Backend result', number_result);

      if (number_result?.is_error || cvc_result?.is_error || exp_date_result?.is_error) {
        console.log('Backend error', number_result, exp_date_result, cvc_result);
        setLoading(false);
        return;
      }

      setSuccess(true);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.log('Error creating secrets', e);
    }
  }

  useEffect(() => {
    const mdb = new Modernbanc(API_KEY);
    const card_number_element = mdb.addElement({ ...number_props, deletion_date });
    const expiry_date_element = mdb.addElement({ ...exp_date_props, deletion_date });
    const cvc_element = mdb.addElement({ ...cvc_props, deletion_date });

    if (card_number_element) {
      card_number_element.mount(`#${card_number_element.id}_container`);
      card_number_element.on('ready', () => {
        setCardNumberReady(true);
      });
      card_number_element.on('change', (e: InputElementEvent<'change'>) => {
        const valid_card_number = e.data.matches?.valid_card_number;
        setValidCardNumber(!!valid_card_number);
        const card_type = e.data.matches?.card_type;
        if (card_type) {
          const card_types = ['visa', 'mastercard', 'american-express'];
          const type = card_types.includes(card_type) ? card_type : 'other';
          setCardType(type as TCardType);
        } else {
          // Determine the card type based on the likely prefix match
          let type: TCardType = 'other'; // Default to 'other'
          if (e.data.matches?.likely_visa) {
            type = 'visa';
          } else if (e.data.matches?.likely_mastercard) {
            type = 'mastercard';
          } else if (e.data.matches?.likely_american_express) {
            type = 'american-express';
          }
          setCardType(type);
        }
      });
    }

    if (expiry_date_element) {
      expiry_date_element.mount(`#${exp_date_props.id}_container`);
      expiry_date_element.on('ready', () => {
        setExpiryDateReady(true);
      });
      expiry_date_element.on('change', (e: InputElementEvent<'change'>) => {
        const valid_expiry_date = e.data.matches?.valid_expiry_date;
        setValidExpiryDate(!!valid_expiry_date);
      });
    }

    if (cvc_element) {
      cvc_element.mount(`#${cvc_props.id}_container`);
      cvc_element.on('ready', () => {
        setCvcReady(true);
      });
      cvc_element.on('change', (e: InputElementEvent<'change'>) => {
        const valid_cvc = e.data.matches?.valid_cvc;
        setValidCvc(!!valid_cvc);
      });
    }

    setMdb(mdb);

    return () => {
      mdb.element_map.delete(card_number_element.id);
      mdb.element_map.delete(expiry_date_element.id);
      mdb.element_map.delete(cvc_element.id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <S.Container>
      <S.Label>Collecting card information</S.Label>
      <S.Form>
        <Header>
          Enter your card details <Chevron />
        </Header>

        <Main data-show={show_form}>
          <Row>
            <S.ElementContainer id={`${number_props.id}_container`} />
            {card_type && <CardLogo type={card_type} />}
          </Row>
          <Row>
            <S.ElementContainer id={`${exp_date_props.id}_container`} />
            <S.ElementContainer id={`${cvc_props.id}_container`} />
          </Row>
          <S.Button disabled={!can_submit} onClick={submit}>
            Submit
          </S.Button>
        </Main>
        <ResultComponent loading={!all_inputs_ready || loading} success={success} />
      </S.Form>
      {/* <S.Button} onClick={() => card_number_element?.focus()}> */}
      {/*   Focus card number */}
      {/* </S.Button> */}
      {/* <S.Button onClick={() => card_number_element?.blur()}> */}
      {/*   Blur card number */}
      {/* </S.Button> */}
    </S.Container>
  );
};

export default Input;
